(function () {
  'use strict';

  var WIDTH_PARAM = 'filter.p.m.custom.width_mm';
  var MIN_PARAM = 'krd_width_min';
  var CAP_PARAM = 'krd_width_max';

  function parseInteger(raw) {
    if (raw === null || raw === undefined || String(raw).trim() === '') return null;
    var value = Number(raw);
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
  }

  function uniqueWidths(rawValues) {
    var seen = new Set();
    return Array.from(rawValues).reduce(function (widths, raw) {
      var number = parseInteger(raw);
      if (number !== null && !seen.has(number)) {
        seen.add(number);
        widths.push({ value: String(raw).trim(), number: number });
      }
      return widths;
    }, []).sort(function (a, b) { return a.number - b.number; });
  }

  // Keep the cap argument first for compatibility with existing maximum-only URLs.
  function selectedValues(widths, cap, min) {
    if (min === undefined) min = null;
    if (cap === null && min === null) return [];
    var values = widths.filter(function (width) {
      return (min === null || width.number >= min) && (cap === null || width.number <= cap);
    }).map(function (width) { return width.value; });
    return values.length ? values : ['0'];
  }

  function nativeValues(params) {
    return params.getAll(WIDTH_PARAM).reduce(function (values, raw) {
      return values.concat(raw.split(',').map(function (value) { return value.trim(); })
        .filter(function (value) { return value !== ''; }));
    }, []);
  }

  function readBound(params, name) {
    var values = params.getAll(name);
    return values.length === 1 ? parseInteger(values[0]) : null;
  }

  function readState(params) {
    var min = readBound(params, MIN_PARAM);
    var cap = readBound(params, CAP_PARAM);
    var widthValues = nativeValues(params);
    var enhanced = min !== null || cap !== null || widthValues.length === 0;
    return { min: min, cap: cap, enhanced: enhanced, legacy: !enhanced, widthValues: widthValues };
  }

  function canonicalValues(values) {
    return Array.from(new Set(values.map(function (raw) {
      var number = parseInteger(raw);
      return number === null ? String(raw).trim() : String(number);
    }).filter(function (value) { return value !== ''; }))).sort();
  }

  function sameValues(a, b) {
    return JSON.stringify(canonicalValues(a)) === JSON.stringify(canonicalValues(b));
  }

  function removeWidth(input, base) {
    var url = new URL(String(input), base);
    [WIDTH_PARAM, MIN_PARAM, CAP_PARAM, 'page'].forEach(function (name) { url.searchParams.delete(name); });
    return url;
  }

  function rangeBounds(widths, cap, floor) {
    if (floor === undefined) floor = null;
    var min = widths.length ? widths[0].number : 0;
    var max = widths.length ? widths[widths.length - 1].number : 1;
    var maxValue = cap === null ? max : Math.min(max, Math.max(min, cap));
    var minValue = floor === null ? min : Math.min(max, Math.max(min, floor));
    // Invalid numeric drafts may temporarily cross; handles themselves never do.
    minValue = Math.min(minValue, maxValue);
    return { min: min, max: max, value: maxValue, minValue: minValue, maxValue: maxValue };
  }

  function hasRange(state) {
    return (state.min !== null && state.min !== undefined) || (state.cap !== null && state.cap !== undefined);
  }

  var api = {
    WIDTH_PARAM: WIDTH_PARAM,
    MIN_PARAM: MIN_PARAM,
    CAP_PARAM: CAP_PARAM,
    parseInteger: parseInteger,
    uniqueWidths: uniqueWidths,
    selectedValues: selectedValues,
    readState: readState,
    removeWidth: removeWidth,
    sameValues: sameValues,
    rangeBounds: rangeBounds
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var initialized = new WeakMap();
  var groups = [];
  var reconciledUrls = new Set();
  var lastInitUrl = null;
  var pendingFocus = false;

  function currentUrl() { return new URL(window.location.href); }

  function scopeOf(group) {
    return group.root.closest('[data-section-type="collection-grid"]') || document;
  }

  function inputsOf(group) {
    return Array.from(group.native.querySelectorAll('input[name="' + WIDTH_PARAM + '"]'));
  }

  function widthsOf(group) {
    return uniqueWidths(inputsOf(group).filter(function (input) {
      return input.dataset.krdWidthSynthetic !== 'true';
    }).map(function (input) { return input.value; })).filter(function (width) {
      return width.number > 0;
    });
  }

  function setError(group, invalid, message, input) {
    [group.minNumber, group.number].forEach(function (field) {
      field.setAttribute('aria-invalid', invalid && (!input || input === field) ? 'true' : 'false');
    });
    if (group.error) {
      group.error.textContent = invalid ? message || group.root.dataset.invalidLabel : '';
      group.error.hidden = !invalid;
    }
  }

  function showControl(group, state, preserveInput) {
    var bounds = rangeBounds(group.widths, state.cap, state.min);
    if (!preserveInput) {
      group.minNumber.value = state.min === null ? String(bounds.min) : String(state.min);
      group.number.value = state.cap === null ? String(bounds.max) : String(state.cap);
    }
    [group.minNumber, group.number].forEach(function (input) {
      input.min = '0';
      input.step = '1';
      input.removeAttribute('max');
    });
    [group.minRange, group.range].forEach(function (input) {
      input.min = String(bounds.min);
      input.max = String(bounds.max);
      input.step = '1';
    });
    group.minRange.value = String(bounds.minValue);
    group.range.value = String(bounds.maxValue);
    group.minRange.setAttribute('aria-valuetext', bounds.minValue + ' mm');
    group.range.setAttribute('aria-valuetext', bounds.maxValue + ' mm');
    var span = bounds.max - bounds.min;
    var start = span ? (bounds.minValue - bounds.min) / span * 100 : 0;
    var end = span ? (bounds.maxValue - bounds.min) / span * 100 : 100;
    if (group.track) {
      group.track.style.setProperty('--krd-width-start', start + '%');
      group.track.style.setProperty('--krd-width-end', end + '%');
    }
    // At an endpoint the inward-moving handle must stay reachable.
    group.minRange.style.zIndex = bounds.minValue === bounds.max ? '4' : '3';
    group.range.style.zIndex = bounds.maxValue === bounds.min ? '4' : '3';
    setError(group, false);
  }

  function syncNative(group, state) {
    var values = selectedValues(group.widths, state.cap, state.min);
    var inputs = inputsOf(group);
    values.forEach(function (value) {
      if (inputs.some(function (input) { return sameValues([input.value], [value]); })) return;
      var input = document.createElement('input');
      input.type = 'checkbox';
      input.name = WIDTH_PARAM;
      input.value = value;
      input.hidden = true;
      input.dataset.krdWidthSynthetic = 'true';
      group.native.appendChild(input);
      inputs.push(input);
    });
    inputs.forEach(function (input) {
      input.checked = values.some(function (value) { return sameValues([input.value], [value]); });
      if (input.dataset.krdWidthSynthetic === 'true') input.disabled = !input.checked;
    });
    group.minMeta.value = state.min === null ? '' : String(state.min);
    group.minMeta.disabled = state.min === null;
    group.meta.value = state.cap === null ? '' : String(state.cap);
    group.meta.disabled = state.cap === null;
  }

  function showGroup(group, state) {
    group.root.dataset.krdWidthEnhanced = state.enhanced ? 'true' : 'false';
    group.control.hidden = !state.enhanced;
    group.native.hidden = state.enhanced;
    if (group.heading) group.heading.textContent = state.enhanced ? group.root.dataset.rangeLabel || group.root.dataset.maximumLabel : group.root.dataset.nativeLabel;
    if (state.enhanced) {
      showControl(group, state);
      syncNative(group, state);
    } else {
      [group.minMeta, group.meta].forEach(function (input) { input.value = ''; input.disabled = true; });
      setError(group, false);
    }
  }

  function chipText(group, state) {
    if (state.min !== null && state.cap !== null) {
      return (group.root.dataset.rangeChipLabel || '').replace('[min]', String(state.min)).replace('[max]', String(state.cap));
    }
    if (state.min !== null) return (group.root.dataset.minChipLabel || '').replace('[value]', String(state.min));
    return (group.root.dataset.chipLabel || '').replace('[value]', String(state.cap));
  }

  function updateChips(state) {
    var active = hasRange(state);
    var scopes = new Set(groups.map(scopeOf));
    scopes.forEach(function (scope) {
      scope.querySelectorAll('[data-krd-width-native-chip]').forEach(function (chip) {
        chip.hidden = state.enhanced && active;
      });
      scope.querySelectorAll('[data-krd-width-max-chip]').forEach(function (chip) {
        chip.hidden = !state.enhanced || !active;
        var anchor = chip.querySelector('[data-krd-width-remove]');
        if (!anchor) return;
        anchor.href = removeWidth(anchor.getAttribute('href') || window.location.href, window.location.href).href;
        if (active) {
          var group = groups.find(function (item) { return scopeOf(item) === scope; });
          anchor.textContent = chipText(group, state);
        }
      });
      scope.querySelectorAll('[data-krd-width-filter-count]').forEach(function (button) {
        var total = parseInteger(button.dataset.totalCount) || 0;
        var widthCount = parseInteger(button.dataset.widthCount) || 0;
        var count = state.enhanced ? Math.max(0, total - widthCount) + (active ? 1 : 0) : total;
        var label = button.querySelector('[data-krd-width-filter-count-value]');
        if (label) {
          label.textContent = count ? '(' + count + ')' : '';
          label.hidden = count === 0;
        }
        button.classList.toggle('btn--tertiary-active', count > 0);
      });
    });
  }

  function dispatchApply(group, min, cap, manual) {
    if (manual !== false && group.root.closest('#FilterDrawer')) pendingFocus = true;
    var state = { min: min, cap: cap, enhanced: true };
    groups.forEach(function (item) { showGroup(item, state); });
    updateChips(state);
    group.form.dispatchEvent(new CustomEvent('krd:width-apply', {
      bubbles: true,
      detail: { minWidth: min, maxWidth: cap }
    }));
  }

  function inputState(group) {
    return { min: parseInteger(group.minNumber.value), cap: parseInteger(group.number.value) };
  }

  function commit(group) {
    var state = inputState(group);
    var invalid = [group.minNumber, group.number].find(function (input) {
      return (input.value.trim() !== '' && parseInteger(input.value) === null) || (input.validity && input.validity.badInput);
    });
    if (invalid) {
      setError(group, true, null, invalid);
      invalid.focus();
      return;
    }
    var bounds = rangeBounds(group.widths, null, null);
    if (state.cap !== null && group.widths.length && state.cap < bounds.min) {
      setError(group, true, (group.root.dataset.belowMinLabel || group.root.dataset.invalidLabel || '').replace('[value]', String(bounds.min)), group.number);
      group.number.focus();
      return;
    }
    if (state.min !== null && group.widths.length && state.min > bounds.max) {
      setError(group, true, (group.root.dataset.aboveMaxLabel || group.root.dataset.invalidLabel || '').replace('[value]', String(bounds.max)), group.minNumber);
      group.minNumber.focus();
      return;
    }
    if (state.min !== null && state.cap !== null && state.min > state.cap) {
      setError(group, true, group.root.dataset.orderLabel || group.root.dataset.invalidLabel, group.minNumber);
      group.minNumber.focus();
      return;
    }
    // The displayed catalog endpoints mean an unconstrained side, as does a blank.
    if (state.min === bounds.min) state.min = null;
    if (state.cap === bounds.max) state.cap = null;
    dispatchApply(group, state.min, state.cap);
  }

  function bindGroup(root) {
    var group = initialized.get(root);
    if (group) {
      group.widths = widthsOf(group);
      return group;
    }
    group = {
      root: root,
      form: root.closest('.filter-form'),
      control: root.querySelector('[data-krd-width-control]'),
      native: root.querySelector('[data-krd-width-native]'),
      minNumber: root.querySelector('[data-krd-width-min-number]'),
      number: root.querySelector('[data-krd-width-number]'),
      minRange: root.querySelector('[data-krd-width-min-range]'),
      range: root.querySelector('[data-krd-width-range]'),
      minMeta: root.querySelector('input[name="' + MIN_PARAM + '"]'),
      meta: root.querySelector('input[name="' + CAP_PARAM + '"]'),
      apply: root.querySelector('[data-krd-width-apply]'),
      reset: root.querySelector('[data-krd-width-reset]'),
      heading: root.querySelector('[data-krd-width-heading]'),
      error: root.querySelector('[data-krd-width-error]'),
      track: root.querySelector('[data-krd-width-track]')
    };
    if (!group.form || !group.control || !group.native || !group.minNumber || !group.number || !group.minRange || !group.range || !group.minMeta || !group.meta || !group.apply || !group.reset) return null;
    group.widths = widthsOf(group);
    [group.minNumber, group.number].forEach(function (input) {
      input.addEventListener('input', function (event) {
        event.stopPropagation();
        showControl(group, inputState(group), true);
      });
    });
    group.minRange.addEventListener('input', function (event) {
      event.stopPropagation();
      var min = parseInteger(group.minRange.value);
      var cap = parseInteger(group.range.value);
      if (min === null || cap === null) return;
      min = Math.min(min, cap);
      group.minNumber.value = String(min);
      showControl(group, { min: min, cap: parseInteger(group.number.value) }, true);
    });
    group.range.addEventListener('input', function (event) {
      event.stopPropagation();
      var min = parseInteger(group.minRange.value);
      var cap = parseInteger(group.range.value);
      if (min === null || cap === null) return;
      cap = Math.max(min, cap);
      group.number.value = String(cap);
      showControl(group, { min: parseInteger(group.minNumber.value), cap: cap }, true);
    });
    [group.minNumber, group.number, group.minRange, group.range].forEach(function (input) {
      input.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        commit(group);
      });
    });
    group.apply.addEventListener('click', function (event) {
      event.preventDefault();
      commit(group);
    });
    group.reset.addEventListener('click', function (event) {
      event.preventDefault();
      dispatchApply(group, null, null);
    });
    initialized.set(root, group);
    return group;
  }

  function init() {
    groups = Array.from(document.querySelectorAll('[data-krd-width-group]'))
      .map(bindGroup).filter(Boolean);
    if (!groups.length) return;
    var snapshot = window.location.href;
    if (snapshot !== lastInitUrl) {
      reconciledUrls.clear();
      lastInitUrl = snapshot;
    }
    var state = readState(currentUrl().searchParams);
    groups.forEach(function (group) { showGroup(group, state); });
    updateChips(state);
    if (!state.enhanced || !hasRange(state)) return;
    var expected = selectedValues(groups[0].widths, state.cap, state.min);
    var attempt = snapshot + '|' + JSON.stringify(canonicalValues(expected));
    if (sameValues(state.widthValues, expected) || reconciledUrls.has(attempt)) return;
    var schedule = window.queueMicrotask || function (callback) { window.setTimeout(callback, 0); };
    schedule(function () {
      if (window.location.href !== snapshot || !groups.length || !groups[0].root.isConnected) return;
      var current = readState(currentUrl().searchParams);
      if (!current.enhanced || current.min !== state.min || current.cap !== state.cap || reconciledUrls.has(attempt)) return;
      reconciledUrls.add(attempt);
      dispatchApply(groups[0], state.min, state.cap, false);
    });
  }

  document.addEventListener('click', function (event) {
    var anchor = event.target.closest && event.target.closest('a');
    if (!anchor || !groups.length) return;
    var scope = groups.map(scopeOf).find(function (item) { return item.contains(anchor); });
    if (!scope) return;
    var url = new URL(anchor.href, window.location.href);
    var current = currentUrl();
    if (anchor.matches('[data-krd-width-remove]')) {
      anchor.href = removeWidth(url).href;
      groups.forEach(function (group) { showGroup(group, { min: null, cap: null, enhanced: true }); });
      updateChips({ min: null, cap: null, enhanced: true });
    } else if (url.origin === current.origin && url.pathname === current.pathname) {
      var state = readState(current.searchParams);
      if (state.enhanced && hasRange(state) && nativeValues(url.searchParams).length) {
        if (state.min !== null) url.searchParams.set(MIN_PARAM, String(state.min));
        else url.searchParams.delete(MIN_PARAM);
        if (state.cap !== null) url.searchParams.set(CAP_PARAM, String(state.cap));
        else url.searchParams.delete(CAP_PARAM);
      } else if (!nativeValues(url.searchParams).length) {
        url.searchParams.delete(MIN_PARAM);
        url.searchParams.delete(CAP_PARAM);
      }
      anchor.href = url.href;
    }
  }, true);

  api.init = init;
  window.KrdWidthFilter = api;
  document.addEventListener('collection:reloaded', function () {
    init();
    if (!pendingFocus) return;
    pendingFocus = false;
    var button = document.querySelector('[data-krd-width-filter-count]');
    if (button) button.focus({ preventScroll: true });
  });
  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
